import { Component, EventListener } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import $ from 'jquery';

export interface IHorizontalNavConfig {
	animationSpeed: number | string;
	indicatorArrowOffset: number;
	listItemOffset: number;
	scrollFactor?: number;
	selectors: {
		[key: string]: string;
	};
}

enum Selectors {
	ScrollableArea = '.js-m-horizontal-nav__scroll-area',
	NavigationItemLeft = '.js-m-horizontal-nav__nav-item-left',
	NavigationItemRight = '.js-m-horizontal-nav__nav-item-right',
}

/**
 * @author jwidmer
 */
@Component('HorizontalNav')
export class HorizontalNav extends GondelJqueryComponent {
	private $window: JQuery<Window> = $(window);
	private $scrollableArea: JQuery;
	private $buttonLeft: JQuery;
	private $buttonRight: JQuery;
	private config: IHorizontalNavConfig;

	public start() {
		this.config = {
			animationSpeed: this.$ctx.data('animation-speed') || 'slow',
			indicatorArrowOffset: this.$ctx.data('arrow-offset') || 60,
			listItemOffset: this.$ctx.data('list-item-offset') || 0,
			scrollFactor: this.$ctx.data('scroll-factor') || 2,
			selectors: {
				activeItemSelector: `.${this.$ctx.data('active-item-selector')}`,
			},
		};

		this.$scrollableArea = this.$ctx.find(Selectors.ScrollableArea) || null;
		this.$buttonLeft = this.$ctx.find(Selectors.NavigationItemLeft);
		this.$buttonRight = this.$ctx.find(Selectors.NavigationItemRight);

		if (!this.$scrollableArea) {
			throw new Error(
				`Can not find "${Selectors.ScrollableArea}" inside horizontal-nav, does the element really exist?`
			);
		}

		// event cannot be attached via annotation because scroll event does not bubble
		this.$scrollableArea.on('scroll', () => {
			// anonymous function needed to have correct "this" context
			this._handleScrollAreaScrolling();
		});

		// event cannot be attached via annotation because scroll event does not bubble
		this.$window.on('resize', () => {
			// anonymous function needed to have correct "this" context
			this._handleScrollAreaScrolling();
		});
	}

	public stop() {
		this.$scrollableArea.off('scroll');
		this.$window.off('resize');
	}

	public initializeScrollBehaviour() {
		this.checkScrollIndicators();

		if (this.config.selectors.activeItemSelector) {
			const $activeItem = this.$scrollableArea.find(`${this.config.selectors.activeItemSelector}`);

			if ($activeItem.length === 1) {
				this.horizontallyScrollToItem($activeItem);
			}
		}
	}

	/**
	 * Scroll by a predefined amount
	 *
	 * @public
	 * @param {string} direction The direction to scroll to
	 * @returns {void}
	 */
	public horizontallyScroll(direction: string) {
		const scrollableWidth = this.$scrollableArea.width()!;
		const distance = scrollableWidth / 2;
		const currentLeftPosition = this.$scrollableArea.scrollLeft()!;
		const newPos = direction === 'right' ? currentLeftPosition + distance : currentLeftPosition - distance;

		this.$scrollableArea.stop().animate({ scrollLeft: newPos }, this.config.animationSpeed);
	}

	/**
	 * Scroll to provided $item
	 *
	 * @@public
	 * @param {Object} $item The tab item to scroll to
	 * @returns {void}
	 */
	public horizontallyScrollToItem($item: JQuery) {
		const leftOffset = $item.position().left;

		this.$scrollableArea.animate(
			{ scrollLeft: leftOffset - this.config.indicatorArrowOffset },
			this.config.animationSpeed
		);
	}

	/**
	 * Scroll to provided $item and take item width in count
	 *
	 * @@public
	 * @param {Object} $item The item to scroll to
	 * @param {boolean} noAnimation defines if the scrollTo should be animated
	 * @returns {void}
	 */
	public horizontallyScrollToItemAndCenter($item: JQuery, noAnimation: boolean) {
		const leftOffset = $item.offset()!.left;
		const windowWidth = this.$window.width()!;
		const itemWidth = $item.width()!;

		if (!noAnimation) {
			this.$scrollableArea.animate(
				{ scrollLeft: leftOffset - windowWidth / 2 - itemWidth / 2 },
				this.config.animationSpeed
			);
		} else {
			this.$scrollableArea.scrollLeft(leftOffset - windowWidth / 2 + itemWidth / 2);
		}
	}

	/**
	 * Event when the left scroll indicator has been clicked
	 *
	 * @private
	 * @param {Event} ev The click event
	 * @returns {void}
	 */
	@EventListener('click', Selectors.NavigationItemLeft)
	public _handleScrollIndicatorLeftClick(ev) {
		ev.preventDefault();
		this.horizontallyScroll('left');
	}

	/**
	 * Event when the right scroll indicator has been clicked
	 *
	 * @private
	 * @param {Event} ev The click event
	 * @returns {void}
	 */
	@EventListener('click', Selectors.NavigationItemRight)
	public _handleScrollIndicatorRightClick(ev) {
		ev.preventDefault();
		this.horizontallyScroll('right');
	}

	/**
	 * Event when the user scrolls through the list by himself
	 *
	 * @private
	 * @returns {void}
	 */
	@EventListener('scroll', Selectors.ScrollableArea)
	public _handleScrollAreaScrolling() {
		this.checkScrollIndicators();
	}

	/**
	 * Initialise the scroll indicators
	 *
	 * @private
	 * @returns {void}
	 */
	private checkScrollIndicators = () => {
		const scrollWidth = this.$scrollableArea[0].scrollWidth;
		const scrollAreaWidthIsNumber = typeof this.$scrollableArea.innerWidth() === 'number';
		const scrollAreaWidth = this.$scrollableArea.innerWidth() || 0;
		const scrollAreaWidthRounded = scrollAreaWidthIsNumber ? Math.ceil(scrollAreaWidth) : 0;
		const offset = 2 * this.config.listItemOffset;
		const scrollLeft = this.$scrollableArea[0].scrollLeft;
		const distanceLeft = scrollWidth - scrollAreaWidthRounded - scrollLeft - offset;

		if (scrollWidth <= scrollAreaWidthRounded + offset) {
			this.$buttonLeft.hide();
			this.$buttonRight.hide();
			return;
		}

		if (scrollLeft <= 0) {
			this.$buttonLeft.fadeOut(this.config.animationSpeed);
		} else {
			this.$buttonLeft.fadeIn(this.config.animationSpeed);
		}

		if (distanceLeft > 0) {
			this.$buttonRight.fadeIn(this.config.animationSpeed);
		} else {
			this.$buttonRight.fadeOut(this.config.animationSpeed);
		}
	};
}
